import { Alert, AlertIcon, Box, Button } from '@chakra-ui/react';
import { journalEntriesMolecule } from '@web-app/atoms/in-session/journalEntriesMolecule';
import { JournalTimeline } from '@web-app/components/JournalTimeline/JournalTimeline/JournalTimeline';
import { useJournalEntries } from '@web-app/hooks/use-journal-entries';
import { useMolecule } from 'bunshi/react';
import { useAtomValue } from 'jotai';
import { FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

// oxlint-disable-next-line @typescript-eslint/no-empty-object-type
export interface JournalEntriesTabProps {}

/**
 * Journal Entries tab content for the CallV2 SidePane.
 * Displays patient journal entries (food, water, symptoms, stool, weight)
 * in a timeline view with infinite scroll.
 */
export const JournalEntriesTab: FC<JournalEntriesTabProps> = () => {
  const { lastAppointmentDateAtom } = useMolecule(journalEntriesMolecule);
  const lastAppointmentDate = useAtomValue(lastAppointmentDateAtom);

  // Fetch journal entries using Jotai + React Query
  const {
    entries,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadMore,
    refetch,
  } = useJournalEntries();

  // Error state
  if (error) {
    return (
      <Box p={4}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <Box fontWeight="bold">Failed to load journal entries</Box>
            <Box fontSize="sm" mt={1}>
              {error.message || 'An unexpected error occurred'}
            </Box>
          </Box>
          <Button size="sm" onClick={() => refetch()} ml={2}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  // Main content
  return (
    <Box
      p={4}
      height="100%"
      overflowY="auto"
      data-testid="journal-entries-tab-content"
    >
      <ErrorBoundary
        fallback={
          <Box p={0}>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box flex="1">
                <Box fontWeight="bold">Journal timeline unavailable</Box>
                <Box fontSize="sm" mt={1}>
                  Something went wrong while displaying entries. Try leaving
                  this tab and opening it again.
                </Box>
              </Box>
            </Alert>
          </Box>
        }
      >
        <JournalTimeline
          entries={entries}
          onLoadMore={hasMore ? loadMore : undefined}
          hasMore={hasMore}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          lastAppointmentDate={lastAppointmentDate}
        />
      </ErrorBoundary>
    </Box>
  );
};

JournalEntriesTab.displayName = 'JournalEntriesTab';
